import { apiClient } from '../lib/apiClient';

/**
 * Equipment safety of an aquarium (Reef Guard #1828): the safety checklist
 * (yes / no / not applicable per item) and the maintenance plans — editable
 * templates that create a recurring 'equipment' task when switched on.
 */
export type SafetyAnswer = 'yes' | 'no' | 'na';

export interface SafetyChecklistEntry {
  readonly key: string;
  /** null = not answered yet. */
  readonly answer: SafetyAnswer | null;
}

export interface MaintenancePlan {
  readonly key: string;
  readonly defaultIntervalDays: number;
  readonly enabled: boolean;
  readonly intervalDays: number | null;
  readonly taskId: string | null;
}

export interface EquipmentSafety {
  readonly checklist: SafetyChecklistEntry[];
  readonly maintenance: MaintenancePlan[];
  readonly source: string;
}

const base = (aquariumId: string): string => `/api/v1/aquariums/${aquariumId}/equipment-safety`;

export function equipmentSafetyKey(aquariumId: string | null | undefined): readonly unknown[] {
  return ['equipmentSafety', aquariumId];
}

/** The checklist items that need attention: answered "no" or not answered yet. */
export function openChecklistItems(safety: EquipmentSafety): SafetyChecklistEntry[] {
  return safety.checklist.filter((c) => c.answer === null || c.answer === 'no');
}

/** GET …/equipment-safety */
export async function getEquipmentSafety(aquariumId: string): Promise<EquipmentSafety> {
  const response = await apiClient.get<{ data: EquipmentSafety }>(base(aquariumId));
  return response.data;
}

/** PUT …/equipment-safety/checklist/:itemKey — null clears the answer. */
export async function saveSafetyAnswer(aquariumId: string, itemKey: string, answer: SafetyAnswer | null): Promise<EquipmentSafety> {
  const response = await apiClient.put<{ data: EquipmentSafety }>(`${base(aquariumId)}/checklist/${encodeURIComponent(itemKey)}`, { answer });
  return response.data;
}

/** PUT …/equipment-safety/maintenance/:templateKey — switch on or change the interval (title = task title). */
export async function saveMaintenancePlan(
  aquariumId: string,
  templateKey: string,
  intervalDays: number,
  title: string,
): Promise<EquipmentSafety> {
  const response = await apiClient.put<{ data: EquipmentSafety }>(`${base(aquariumId)}/maintenance/${encodeURIComponent(templateKey)}`, {
    interval_days: intervalDays,
    title,
  });
  return response.data;
}

/** DELETE …/equipment-safety/maintenance/:templateKey — switch off (the task series is archived). */
export async function deleteMaintenancePlan(aquariumId: string, templateKey: string): Promise<EquipmentSafety> {
  const response = await apiClient.delete<{ data: EquipmentSafety }>(`${base(aquariumId)}/maintenance/${encodeURIComponent(templateKey)}`);
  return response.data;
}
